import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, Search, ShoppingCart, X, Truck } from "lucide-react";
import Logo from "./Logo";
import { useCart } from "../context/CartContext";
import { TRUST_TICKER } from "../mock";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/about", label: "About" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

const TrustTicker = () => (
  <div className="bg-blue-700 text-white text-xs overflow-hidden">
    <div className="flex whitespace-nowrap py-2">
      <div className="fesx-marquee flex shrink-0">
        {[...TRUST_TICKER, ...TRUST_TICKER].map((t, i) => (
          <span key={`${t}-${i}`} className="flex items-center gap-2 px-6 font-medium tracking-wide">
            <span className="h-1.5 w-1.5 rounded-full bg-sky-300" />
            {t}
          </span>
        ))}
      </div>
    </div>
  </div>
);

const Header = () => {
  const { count } = useCart();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(`/shop?q=${encodeURIComponent(query)}`);
    setOpen(false);
  };

  return (
    <header className="sticky top-0 z-50">
      <TrustTicker />
      <div className="bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between gap-4">
            <Logo size="md" />

            <nav className="hidden md:flex items-center gap-1">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  className={({ isActive }) =>
                    `px-3.5 py-2 rounded-md text-sm font-semibold transition-colors ${
                      isActive
                        ? "text-blue-700 bg-blue-50"
                        : "text-slate-600 hover:text-blue-700 hover:bg-slate-50"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </nav>

            <div className="flex items-center gap-2">
              <form onSubmit={submitSearch} className="hidden lg:flex items-center relative">
                <Search className="absolute left-3 h-4 w-4 text-slate-400" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search compounds…"
                  className="w-52 rounded-full border border-slate-200 bg-slate-50 pl-9 pr-4 py-2 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition"
                />
              </form>

              <Link
                to="/cart"
                className="relative inline-flex items-center justify-center h-10 w-10 rounded-full text-slate-700 hover:bg-slate-100 transition-colors"
                aria-label="Cart"
              >
                <ShoppingCart className="h-5 w-5" />
                {count > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 h-5 w-5 rounded-full bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center ring-2 ring-white">
                    {count}
                  </span>
                )}
              </Link>

              {/* Mobile menu */}
              <div className="md:hidden">
                <Sheet open={open} onOpenChange={setOpen}>
                  <SheetTrigger asChild>
                    <button
                      className="inline-flex items-center justify-center h-10 w-10 rounded-full text-slate-700 hover:bg-slate-100"
                      aria-label="Menu"
                    >
                      <Menu className="h-5 w-5" />
                    </button>
                  </SheetTrigger>
                  <SheetContent side="right" className="w-[300px] p-0">
                    <div className="p-5 border-b border-slate-200">
                      <Logo size="sm" />
                    </div>
                    <form onSubmit={submitSearch} className="p-5 border-b border-slate-200">
                      <div className="relative">
                        <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                        <input
                          value={query}
                          onChange={(e) => setQuery(e.target.value)}
                          placeholder="Search compounds…"
                          className="w-full rounded-lg border border-slate-200 bg-slate-50 pl-9 pr-3 py-2 text-sm outline-none focus:border-blue-400"
                        />
                      </div>
                    </form>
                    <nav className="flex flex-col p-3">
                      {NAV.map((n) => (
                        <NavLink
                          key={n.to}
                          to={n.to}
                          onClick={() => setOpen(false)}
                          className={({ isActive }) =>
                            `px-4 py-3 rounded-lg text-base font-semibold ${
                              isActive ? "text-blue-700 bg-blue-50" : "text-slate-700 hover:bg-slate-50"
                            }`
                          }
                        >
                          {n.label}
                        </NavLink>
                      ))}
                    </nav>
                    <div className="mt-auto p-5 text-xs text-slate-500 flex items-center gap-2">
                      <Truck className="h-4 w-4 text-blue-600" />
                      Free express shipping over $220 AUD
                    </div>
                  </SheetContent>
                </Sheet>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
